import React, { FC, ReactNode } from "react";
import { Radio, RadioGroup } from "@UI/Components/Radio/Radio";
import { Flex } from "@UI/Components/Flex/Flex";
import { SimpleGrid } from "@UI/Components/SimpleGrid/SimpleGrid";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from "@UI/Components/Form/Form";
import { Text } from "@UI/Components/Text/Text";
import { StackProps, VStack } from "@UI/Components/Stack/Stack";
import { OptionCard } from "../OptionCard/OptionCard";
import { getOptionControlProps } from "../OptionCard/controlStyles";
import { QuestionFormControl } from "../QuestionFormControl/QuestionFormControl";
import { BaseQuestionProps } from "../types";

export interface SingleSelectOption {
  value: string;
  label: string;
  badge?: ReactNode;
  /** Node pinned to the right of the row — its own control, not part of the label. */
  trailing?: ReactNode;
  /**
   * `trailing`'s own validation message, e.g. the date pill's "required" or
   * "in the past". Rendered below the whole card grid.
   */
  trailingError?: string;
  /** Second line under the label, e.g. the audience options' `option_hints`. */
  hint?: string;
}

export interface SingleSelectQuestionProps extends BaseQuestionProps {
  name: string;
  label?: string;
  options: SingleSelectOption[];
  value?: string;
  onChange: (value: string) => void;
  /** Gap between cards. Defaults to the question screens' 12px; the
   * subcategory picker's design asks for 16px. */
  spacing?: StackProps["spacing"];
  /** 1 (default) stacks the cards; the availability screen wants 2 on desktop. */
  columns?: number;
  /**
   * Fired when the card/row of an option carrying `trailing` is clicked, so a
   * click anywhere on that row (not just the trailing control itself) can
   * activate it — e.g. opening the date pill's native picker.
   */
  onOptionActivate?: (value: string) => void;
}

export const SingleSelectQuestion: FC<SingleSelectQuestionProps> = ({
  name,
  label,
  options,
  value,
  onChange,
  isRequired = false,
  isInvalid = false,
  errorMessage = "",
  isDisabled = false,
  spacing = 3,
  columns = 1,
  onOptionActivate,
}) => (
  <QuestionFormControl
    isRequired={isRequired}
    isInvalid={isInvalid}
    errorMessage={errorMessage}
    isDisabled={isDisabled}
  >
    {label && <FormLabel>{label}</FormLabel>}
    <RadioGroup
      name={name}
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
    >
      <SimpleGrid columns={columns} spacing={spacing}>
        {options.map((option) => {
          const isSelected = value === option.value;
          const control = (
            <Radio
              value={option.value}
              colorScheme="seaGreen"
              spacing="10px"
              flex={option.trailing ? "1" : undefined}
              {...getOptionControlProps(isSelected)}
            >
              {option.hint ? (
                /* Two lines: the label goes bold and the hint sits under it, per
                 * the visibility screen's design. */
                <VStack alignItems="flex-start" spacing="2px">
                  <Text fontWeight="semibold" color="gray.800">
                    {option.label}
                  </Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="normal">
                    {option.hint}
                  </Text>
                </VStack>
              ) : (
                option.label
              )}
            </Radio>
          );

          return (
            <OptionCard
              key={option.value}
              isSelected={isSelected}
              isDisabled={isDisabled}
              badge={option.badge}
            >
              {option.trailing ? (
                /* The trailing node sits *outside* the Radio's <label> on purpose: it is a
                 * control of its own, and nesting it in the label would make every click on
                 * it also toggle the radio. The Radio keeps owning the card's padding, so the
                 * row only has to re-add the right-hand padding for the trailing node. The
                 * Flex's own onClick activates the trailing control on any click within the
                 * row (bubbling up from the radio's label too), so picking the option and
                 * opening its trailing control happen in one click. */
                <Flex
                  align="center"
                  justify="space-between"
                  width="100%"
                  paddingRight={getOptionControlProps(isSelected).padding}
                  onClick={() => onOptionActivate?.(option.value)}
                >
                  {control}
                  {option.trailing}
                </Flex>
              ) : (
                control
              )}
            </OptionCard>
          );
        })}
      </SimpleGrid>
      {/* Below the grid, never inside a cell: a message in the cell makes that
          cell taller, and the grid then grows every card in its row with it.
          Aligned under the right-hand column when there are two, since that is
          where the option carrying a trailing control sits. Its own
          FormControl because the outer one's `isInvalid` is this *screen's*
          select error, not the trailing field's, and Chakra hides
          FormErrorMessage unless its own context says invalid. */}
      {options.some((option) => option.trailingError) && (
        <FormControl isInvalid>
          {options.map(
            (option) =>
              option.trailingError && (
                <FormErrorMessage
                  key={option.value}
                  justifyContent={columns > 1 ? "flex-end" : "flex-start"}
                >
                  {option.trailingError}
                </FormErrorMessage>
              )
          )}
        </FormControl>
      )}
    </RadioGroup>
  </QuestionFormControl>
);
